import { useEffect, useState, useCallback, useMemo } from "react";
import { ChevronLeft, ChevronRight, Wand2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";

const pad2 = (n) => String(n).padStart(2, "0");
const toISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const DOW = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

function startOfWeek(d) {
  const x = new Date(d);
  const dow = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - dow);
  x.setHours(0, 0, 0, 0);
  return x;
}

export default function Presenze() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [tecnici, setTecnici] = useState([]);
  const [users, setUsers] = useState([]);
  const [records, setRecords] = useState({});

  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; }), [weekStart]);

  const load = useCallback(() => {
    const start = toISO(week[0]); const end = toISO(week[6]);
    api.get("/presenze", { params: { start, end } }).then((r) => {
      setTecnici(r.data.tecnici);
      setUsers(r.data.users || []);
      const map = {};
      (r.data.records || []).forEach((rec) => { map[`${rec.date}|${rec.tech_id}`] = rec; });
      setRecords(map);
    }).catch((e) => toast.error(apiError(e)));
  }, [week]);
  useEffect(() => { load(); }, [load]);

  const shift = (n) => { const d = new Date(weekStart); d.setDate(d.getDate() + n * 7); setWeekStart(startOfWeek(d)); };

  const [prefilling, setPrefilling] = useState(false);
  const precompila = async () => {
    setPrefilling(true);
    try {
      const r = await api.post("/presenze/precompila", { start: toISO(week[0]), end: toISO(week[6]) });
      toast.success(`Precompilate ${r.data.updated} presenze dai ticket`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setPrefilling(false); }
  };

  const cell = (date, techId) => records[`${date}|${techId}`] || { present: false, hours: 0, teammate_id: "" };
  const save = async (date, techId, patch) => {
    const cur = cell(date, techId);
    const body = { date, tech_id: techId, present: patch.present ?? cur.present, hours: patch.hours ?? cur.hours, teammate_id: patch.teammate_id ?? cur.teammate_id ?? "" };
    setRecords((m) => ({ ...m, [`${date}|${techId}`]: { ...cur, ...body } }));
    try {
      await api.put("/presenze", body);
      if ("teammate_id" in patch) load();
    } catch (e) { toast.error(apiError(e)); load(); }
  };
  const NONE = "__none__";

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader overline="Flux · Amministrazione" title="Calendario Presenze" subtitle="Registra presenza, ore lavorate e compagno di squadra di ogni tecnico per ogni giorno">
        <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
          <button onClick={() => shift(-1)} data-testid="pres-prev" className="rounded p-1.5 hover:bg-secondary"><ChevronLeft size={18} /></button>
          <span className="min-w-[190px] px-3 text-center text-sm font-semibold text-navy" data-testid="pres-week">{toISO(week[0])} → {toISO(week[6])}</span>
          <button onClick={() => shift(1)} data-testid="pres-next" className="rounded p-1.5 hover:bg-secondary"><ChevronRight size={18} /></button>
        </div>
        <Button variant="outline" onClick={() => setWeekStart(startOfWeek(new Date()))} data-testid="pres-today">Questa settimana</Button>
        <Button onClick={precompila} disabled={prefilling} data-testid="pres-precompila"><Wand2 size={16} />{prefilling ? "Calcolo…" : "Precompila da ticket"}</Button>
      </PageHeader>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-secondary text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="sticky left-0 z-10 bg-secondary px-4 py-3 text-left">Tecnico</th>
              {week.map((d, i) => (
                <th key={i} className={cn("px-3 py-3 text-center", i >= 5 && "text-amber-700")}>{DOW[i]}<br /><span className="text-[10px] font-normal">{pad2(d.getDate())}/{pad2(d.getMonth() + 1)}</span></th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tecnici.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Nessun tecnico</td></tr>
            ) : tecnici.map((tc) => (
              <tr key={tc.id} className="border-b border-border/60" data-testid={`pres-row-${tc.name}`}>
                <td className="sticky left-0 z-10 bg-card px-4 py-2 font-semibold text-navy">{tc.name}</td>
                {week.map((d, i) => {
                  const date = toISO(d);
                  const c = cell(date, tc.id);
                  return (
                    <td key={i} className={cn("px-2 py-2 align-top", i >= 5 && "bg-amber-50/50")}>
                      <div className="flex flex-col items-center gap-1">
                        <Input type="number" min="0" step="0.5" placeholder="ore" value={c.hours || ""} data-testid={`pres-hours-${tc.name}-${date}`}
                          onChange={(e) => { const h = parseFloat(e.target.value) || 0; setRecords((m) => ({ ...m, [`${date}|${tc.id}`]: { ...c, hours: h, present: h > 0 } })); }}
                          onBlur={(e) => { const h = parseFloat(e.target.value) || 0; save(date, tc.id, { hours: h, present: h > 0 }); }}
                          className="h-8 w-16 text-center" />
                        <Select value={c.teammate_id || NONE} onValueChange={(v) => save(date, tc.id, { teammate_id: v === NONE ? "" : v })}>
                          <SelectTrigger className="h-7 w-[104px] text-xs" data-testid={`pres-mate-${tc.name}-${date}`}><SelectValue placeholder="Solo" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Solo</SelectItem>
                            {users.filter((u) => u.id !== tc.id).map((u) => (
                              <SelectItem key={u.id} value={u.id}>+ {u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-muted-foreground">Le ore inserite alimentano il calcolo di costo e margine nella pagina <b>Risultati</b>. Nei giorni feriali le prime 8 ore sono a tariffa ordinaria, le ore oltre le 8 a tariffa straordinaria; sabato e domenica usano sempre la tariffa oraria straordinaria. Se il tecnico ha lavorato in squadra, seleziona il <b>compagno</b>: il costo della giornata sarà la <b>somma dei costi orari</b> dei due. <b>Precompila da ticket</b> calcola le ore dalla durata dei ticket completati.</p>
    </div>
  );
}
